import React, { useState, useEffect } from 'react';
import { getEmployeeOverview } from '../../api/users';
import Avatar from '../common/Avatar';
import { useSort, sortRows, SortTh } from '../common/SortHeader';
import './Users.css';

const ROLE_LABELS = { 'team-leader': 'Team Leader', standard: 'Standard', other: 'Other' };

const nameOf = u => u.displayName || u.username;

// Project names usually already contain the NB no. ("#484 Vigilant") — only add it when missing
const projectLabel = p => {
  const nb = p.newbuildingNo ? String(p.newbuildingNo) : '';
  return nb && !String(p.name).includes(nb) ? `#${nb} ${p.name}` : p.name;
};

const getVal = (u, key) => {
  switch (key) {
    case 'name':     return nameOf(u);
    case 'role':     return ROLE_LABELS[u.role] || u.role;
    case 'projects': return u.projects.length;
    default:         return u[key] || null;
  }
};

// Which projects the employees (team-leader / standard / other) are assigned to — foreman and above
export default function EmployeeOverview({ projects = [] }) {
  const [users, setUsers]   = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [error, setError]   = useState('');
  const [view, setView]     = useState('employees');
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('');   // '' all · 'none' not assigned · project id
  const [sort, toggleSort]  = useSort('name');

  useEffect(() => {
    getEmployeeOverview()
      .then(r => setUsers(r.data.map(u => ({ ...u, projects: (u.projects || []).filter(Boolean) }))))
      .catch(() => setError('Could not load the employee overview.'))
      .finally(() => setLoaded(true));
  }, []);

  const q = search.trim().toLowerCase();
  const matchesSearch = u => !q || [u.username, u.displayName, u.employeeNumber].some(f => f?.toLowerCase().includes(q));
  const matchesFilter = u => !filter
    || (filter === 'none' ? u.projects.length === 0 : u.projects.some(p => p._id === filter));
  const rows = sortRows(users.filter(u => matchesSearch(u) && matchesFilter(u)), sort, getVal);

  // By project: every project, plus employees without a project
  const sortedProjects = [...projects].sort((a, b) => projectLabel(a).localeCompare(projectLabel(b), undefined, { numeric: true }));
  const byProject = [
    ...sortedProjects.map(p => ({
      key: p._id, label: projectLabel(p),
      people: users.filter(u => matchesSearch(u) && u.projects.some(x => x._id === p._id)),
    })),
    { key: 'none', label: 'Not assigned', muted: true, people: users.filter(u => matchesSearch(u) && u.projects.length === 0) },
  ];

  const pickProject = id => { setFilter(id); setView('employees'); };
  const sortProps = { sort, onSort: toggleSort };

  return (
    <div className="module-wrap">
      <div className="module-toolbar">
        <span className="module-toolbar-title">Employee Overview</span>
        <div className="eo-toggle">
          <button className={`btn-secondary${view === 'employees' ? ' eo-on' : ''}`} onClick={() => setView('employees')}>By employee</button>
          <button className={`btn-secondary${view === 'projects' ? ' eo-on' : ''}`} onClick={() => setView('projects')}>By project</button>
        </div>
        <input className="module-search" placeholder="Search name…" value={search} onChange={e => setSearch(e.target.value)} />
        {view === 'employees' && (
          <select className="eo-filter" value={filter} onChange={e => setFilter(e.target.value)}>
            <option value="">All projects</option>
            <option value="none">Not assigned</option>
            {sortedProjects.map(p => <option key={p._id} value={p._id}>{projectLabel(p)}</option>)}
          </select>
        )}
      </div>
      <div className="module-body" style={{ padding: '16px 24px' }}>
        {error && <p style={{ color: 'var(--red)', fontSize: 12, marginBottom: 12 }}>{error}</p>}

        {view === 'employees' ? (
          <table className="data-table eo-table">
            <thead>
              <tr>
                <th style={{ width: 40 }}></th>
                <SortTh sortKey="name"           {...sortProps}>Name</SortTh>
                <SortTh sortKey="employeeNumber" {...sortProps}>Employee #</SortTh>
                <SortTh sortKey="role"           {...sortProps}>Role</SortTh>
                <SortTh sortKey="projects"       {...sortProps}>Projects</SortTh>
              </tr>
            </thead>
            <tbody>
              {rows.map(u => (
                <tr key={u._id}>
                  <td><Avatar user={u} size={26} /></td>
                  <td>{nameOf(u)}{u.displayName && <span className="eo-username">{u.username}</span>}</td>
                  <td>{u.employeeNumber || <span className="user-contact-muted">—</span>}</td>
                  <td><span className={`role-badge role-${u.role}`}>{ROLE_LABELS[u.role] || u.role}</span></td>
                  <td>
                    {u.projects.length === 0
                      ? <span className="user-contact-muted">Not assigned</span>
                      : <div className="eo-chips">
                          {[...u.projects].sort((a, b) => projectLabel(a).localeCompare(projectLabel(b), undefined, { numeric: true }))
                            .map(p => <button key={p._id} className="eo-chip" title="Show everyone on this project" onClick={() => setFilter(p._id)}>{projectLabel(p)}</button>)}
                        </div>}
                  </td>
                </tr>
              ))}
              {loaded && rows.length === 0 && (
                <tr><td colSpan={5} className="empty-state">{users.length === 0 ? 'No employees yet.' : 'No employees match.'}</td></tr>
              )}
            </tbody>
          </table>
        ) : (
          <table className="data-table eo-table">
            <thead>
              <tr><th>Project</th><th style={{ width: 70 }}>People</th><th>Employees</th></tr>
            </thead>
            <tbody>
              {byProject.map(g => (
                <tr key={g.key}>
                  <td className={g.muted ? 'user-contact-muted' : 'eo-project-cell'}>
                    {g.muted ? g.label : <button className="eo-link" onClick={() => pickProject(g.key)}>{g.label}</button>}
                  </td>
                  <td>{g.people.length}</td>
                  <td>
                    {g.people.length === 0
                      ? <span className="user-contact-muted">—</span>
                      : <div className="eo-chips">
                          {[...g.people].sort((a, b) => nameOf(a).localeCompare(nameOf(b))).map(u => (
                            <span key={u._id} className="eo-person"><Avatar user={u} size={18} />{nameOf(u)}</span>
                          ))}
                        </div>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
